import { useEffect, useCallback, useRef } from "react";
import { useShallow } from "zustand/react/shallow";
import { useMediaStore } from "@/shared/store/voice-chat";
import { useRealtimeAgentStore } from "@/shared/store/realtime-agent";
import { useAvatarStore } from "@/shared/store/avatar-store";
import { useAudioPlayer } from "@/shared/hooks/audio-player/model/use-audio-player";
import { useVadStore } from "@/shared/store/vad";
import { useManageChunks } from "./use-manage-chunk";
import { getAudioStartAnimation } from "./audio-start-animation";

export const useVoiceChatAudioPlayer = () => {
  // QueueManager 훅 사용
  const { consumeNextChunk } = useManageChunks();

  // Media store 상태 구독 - useRef로 생명주기 영향 방지
  const { isMuted, isSpeakerOn } = useMediaStore(
    useShallow(state => ({
      isMuted: state.isMuted,
      isSpeakerOn: state.isSpeakerOn,
    }))
  );

  // 상태를 useRef로 관리하여 생명주기 영향 방지
  const isMutedRef = useRef(isMuted);
  const isSpeakerOnRef = useRef(isSpeakerOn);
  const activeChunkRef = useRef<{ turn_id?: string; sequence_num: number } | null>(null);

  //생명주기에 영향을 주면 안되는 것.
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  useEffect(() => {
    isSpeakerOnRef.current = isSpeakerOn;
  }, [isSpeakerOn]);

  // Realtime agent store 상태 구독 - 액션들만 구독
  const { clearChunks, setAudioPlaying, setGreetingLoading } = useRealtimeAgentStore(
    useShallow(state => ({
      clearChunks: state.clearChunks,
      setAudioPlaying: state.setAudioPlaying,
      setGreetingLoading: state.setGreetingLoading,
    }))
  );

  const setVadStatus = useVadStore(state => state.setVadStatus);
  const setCurrentAnimation = useAvatarStore(state => state.setCurrentAnimation);


  // useAudioPlayer 훅 사용
  const {
    audioPlayer,
    isInitialized,
    play,
    pause: pauseAudio,
    setVolume,
  } = useAudioPlayer({
    config: {
      autoPlay: false,
      loop: false,
      muted: false,
      isBase64: true
    },
    callbacks: {
      onPlay: () => {
        setAudioPlaying(true);
        const chunk = activeChunkRef.current;
        if (chunk?.turn_id?.startsWith('greeting_') && chunk.sequence_num === 0) {
          setGreetingLoading(false);
        }
        setCurrentAnimation(getAudioStartAnimation(chunk?.turn_id, chunk?.sequence_num ?? 0));
      },
      onPause: () => {
        setAudioPlaying(false);
      },
      onEnded: () => {
        setAudioPlaying(false);
        setGreetingLoading(false);
        consumeNextChunk()
      },
      onError: (error: Event | string) => {
        setAudioPlaying(false);
        setGreetingLoading(false);
        console.error('🔊 오디오 재생 에러:', error);
      }
    }
  });


  useEffect(() => {
    const unsubscribe = useRealtimeAgentStore.subscribe((state) => state.currentChunk, (currentChunk) => {
      if (currentChunk && currentChunk !== "END" && currentChunk?.audio) {
        activeChunkRef.current = currentChunk;
        play(currentChunk.audio);
      } else if (currentChunk === "END" || currentChunk?.is_final) {
        activeChunkRef.current = null;
        setAudioPlaying(false);
        setGreetingLoading(false);
        // TTS 완료 시 VAD listening 설정 (greeting/일반 턴 동일하게 처리)
        setVadStatus("listening");
        if (!isMutedRef.current) {
          setCurrentAnimation('listening');
        } else {
          setCurrentAnimation("toss");
        }
      } else if (currentChunk === null) {
        activeChunkRef.current = null;
        setAudioPlaying(false);
        setGreetingLoading(false);
        pauseAudio();
      }
    });
    return () => unsubscribe();
  }, [play, setAudioPlaying, setGreetingLoading, setCurrentAnimation, setVadStatus, consumeNextChunk, pauseAudio]);

  // 볼륨 상태 동기화 (isSpeakerOnRef 사용)
  useEffect(() => {
    if (isInitialized) {
      const volume = isSpeakerOnRef.current ? 1.0 : 0.0;
      setVolume(volume);
    }
  }, [isSpeakerOn, isInitialized, setVolume]);

  // 리셋 함수 (새로운 대화 시작 시)
  const resetAudioPlayer = useCallback(() => {

    clearChunks();
    activeChunkRef.current = null;
    setAudioPlaying(false);
    setGreetingLoading(false);
    pauseAudio();
  }, [clearChunks, pauseAudio, setAudioPlaying, setGreetingLoading]);

  return {
    // 상태
    isInitialized,
    isMuted,
    isSpeakerOn,

    // 액션
    resetAudioPlayer,
    pauseAudio,

    // 내부 상태 (디버깅용)
    audioPlayer,
  };
};
